import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { insights } from "../i18n/insights";
import { Reveal } from "../components/Motion";

const InsightCard = ({ item, index, testid }) => {
  const { lang } = useLanguage();
  const c = insights[lang];
  return (
    <Reveal delay={index * 0.07}>
      <Link
        to={item.to}
        data-testid={testid}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl"
      >
        <div className="h-44 overflow-hidden">
          <img
            src={item.image}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col p-7">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-navy/5 px-3 py-1 font-mono text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-navy">
              {item.tag}
            </span>
            <span className="font-mono text-[0.62rem] tracking-[0.2em] text-slate-400">{item.date}</span>
          </div>
          <h3 className="mt-4 font-display text-lg font-semibold leading-snug tracking-tight text-ink">
            {item.title}
          </h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{item.excerpt}</p>
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy">
            {c.readMore}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
};

export const InsightsStrip = () => {
  const { lang } = useLanguage();
  const c = insights[lang];
  return (
    <section data-testid="insights-strip" className="bg-mist py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
                {c.homeOverline}
              </p>
              <h2 className="mt-5 max-w-xl font-display text-2xl font-semibold leading-snug tracking-tight text-ink sm:text-3xl lg:text-4xl">
                {c.homeTitle}
              </h2>
            </div>
            <Link
              to="/approfondimenti"
              data-testid="insights-strip-all-link"
              className="group inline-flex items-center gap-2 font-semibold text-navy transition-colors duration-300 hover:text-accent"
            >
              {c.allCta}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {c.items.slice(0, 3).map((item, i) => (
            <InsightCard key={i} item={item} index={i} testid={`insights-strip-card-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Insights() {
  const { lang } = useLanguage();
  const c = insights[lang];
  return (
    <main data-testid="insights-page">
      <section className="relative overflow-hidden bg-paper pt-32 pb-14 md:pt-44 md:pb-20">
        <div className="blueprint-grid absolute inset-0 opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-accent">
              {c.overline}
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              {c.title}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">{c.sub}</p>
          </Reveal>
        </div>
      </section>
      <section className="bg-white pb-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
          {c.items.map((item, i) => (
            <InsightCard key={i} item={item} index={i} testid={`insight-card-${i}`} />
          ))}
        </div>
      </section>
    </main>
  );
}
